import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, X } from 'lucide-react'
import { notify } from './Toast'
import { listCategories, selectMyTier, type CategoryRecord, type TierRecord } from '../api/client'
import { useAuth } from '../context/AuthProvider'

function formatPrice(tier: TierRecord) {
  if (tier.tierCode === 'FREE') return '$0'
  return null
}

function tagline(category: CategoryRecord, tier: TierRecord): string {
  if (tier.description) return tier.description
  const cat = (category.displayName || category.code).toLowerCase()
  if (tier.tierCode === 'FREE') return 'Answers with accurate AI and citations'
  return `${cat} ${tier.tierCode.toLowerCase()} plan`
}

function ctaLabel(tier: TierRecord): string {
  if (tier.tierCode === 'FREE') return 'Continue with Free'
  const name = (tier.displayName || tier.tierCode).toLowerCase()
  return `Get ${name.charAt(0).toUpperCase()}${name.slice(1)}`
}

export default function PlanSelection({ onSelected, onCancel }: {
  onSelected?: () => void
  onCancel?: () => void
}) {
  const queryClient = useQueryClient()
  const { entitlements } = useAuth()
  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['governance-categories'],
    queryFn: listCategories,
  })

  const activeCategories = useMemo(
    () => [...categories].filter((c) => c.enabled).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [categories],
  )

  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [submitting, setSubmitting] = useState<string | null>(null)

  useEffect(() => {
    if (selectedCategory || activeCategories.length === 0) return
    const current = entitlements?.categoryCode
      ? activeCategories.find((c) => c.code === entitlements.categoryCode)
      : undefined
    const preferred = current
      || activeCategories.find((c) => c.code === 'PERSONAL')
      || activeCategories[0]
    setSelectedCategory(preferred.code)
  }, [activeCategories, selectedCategory, entitlements?.categoryCode])

  const currentCategory = activeCategories.find((c) => c.code === selectedCategory)

  const tiers = useMemo(() => {
    const list = (currentCategory?.tiers || []).filter((t) => t.enabled)
    return [...list].sort((a, b) => {
      if (a.tierCode === 'FREE' && b.tierCode !== 'FREE') return -1
      if (b.tierCode === 'FREE' && a.tierCode !== 'FREE') return 1
      return (a.sortOrder || 0) - (b.sortOrder || 0)
    })
  }, [currentCategory])

  const handleSelect = async (categoryCode: string, tierCode: string) => {
    const key = `${categoryCode}/${tierCode}`
    setSubmitting(key)
    try {
      await selectMyTier(categoryCode, tierCode)
      notify.success('Plan selected.')
      await queryClient.invalidateQueries({ queryKey: ['entitlements'] })
      await queryClient.invalidateQueries({ queryKey: ['features'] })
      await queryClient.invalidateQueries({ queryKey: ['my-persona'] })
      onSelected?.()
    } catch (err) {
      notify.error((err as { message?: string })?.message || 'Failed to select plan.')
    } finally {
      setSubmitting(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 size={32} className="animate-spin text-brand-500" />
      </div>
    )
  }

  if (activeCategories.length === 0) {
    return (
      <div className="max-w-md mx-auto py-24 text-center space-y-3">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-50">No plans available</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Plan options haven't been configured yet. Contact your administrator.
        </p>
      </div>
    )
  }

  return (
    <div className="relative max-w-6xl mx-auto py-12 px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 -top-24 -translate-x-1/2 h-96 w-[52rem] bg-brand-500/10 blur-3xl rounded-full" />
      </div>

      {onCancel && (
        <button type="button" onClick={onCancel}
          className="absolute right-4 top-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors z-10"
          aria-label="Close">
          <X size={18} />
        </button>
      )}

      <div className="relative text-center space-y-3">
        <h1 className="text-3xl md:text-4xl font-serif tracking-tight text-gray-900 dark:text-gray-50">
          Select your plan
        </h1>
        <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
          {entitlements?.tierCode
            ? <>You're on <span className="font-medium text-gray-700 dark:text-gray-300">{entitlements.categoryCode} / {entitlements.tierCode}</span>. Switch any time.</>
            : 'Upgrade for a broader experience and premium AI models.'}
        </p>
      </div>

      <div className="relative mt-8 flex justify-center">
        <div className="inline-flex items-center gap-1 p-1 rounded-full border border-gray-200 dark:border-gray-700 bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm">
          {activeCategories.map((c) => {
            const active = c.code === selectedCategory
            return (
              <button key={c.code} type="button" onClick={() => setSelectedCategory(c.code)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  active
                    ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300 ring-1 ring-brand-500/40'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}>
                {c.displayName || c.code}
              </button>
            )
          })}
        </div>
      </div>

      <div className={`relative mt-10 grid gap-5 ${tiers.length >= 3 ? 'md:grid-cols-3' : tiers.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'max-w-md mx-auto'}`}>
        {tiers.map((tier) => {
          const isFree = tier.tierCode === 'FREE'
          const isCurrent = entitlements?.categoryCode === selectedCategory && entitlements?.tierCode === tier.tierCode
          const highlighted = isCurrent || (!entitlements?.tierCode && isFree)
          const price = formatPrice(tier)
          const busy = submitting === `${selectedCategory}/${tier.tierCode}`
          return (
            <div key={tier.tierCode}
              className={`relative flex flex-col rounded-2xl border p-6 md:p-7 transition-colors ${
                highlighted
                  ? 'border-brand-500/50 bg-white dark:bg-gray-800 ring-1 ring-brand-500/40 shadow-lg shadow-brand-500/5'
                  : 'border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-800/60 hover:border-gray-300 dark:hover:border-gray-600'
              }`}>
              {isCurrent && (
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-brand-500/15 text-brand-700 dark:text-brand-300">
                  <Check size={10} /> Current
                </span>
              )}
              <div className="space-y-1.5">
                <div className="flex items-baseline gap-2 font-serif text-xl md:text-2xl tracking-tight text-gray-900 dark:text-gray-50">
                  <span className="opacity-60">{(currentCategory?.displayName || selectedCategory).toLowerCase()}</span>
                  <span>{(tier.displayName || tier.tierCode).toLowerCase()}</span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 min-h-[2.5rem]">
                  {tagline(currentCategory!, tier)}
                </p>
              </div>

              <div className="mt-5 pb-5 border-b border-gray-200 dark:border-gray-700">
                {price ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-gray-900 dark:text-gray-50">{price}</span>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400 dark:text-gray-500">Pricing available at checkout</div>
                )}
              </div>

              <ul className="mt-5 space-y-2.5 flex-1">
                {(isFree
                  ? ['Good for limited daily use', 'Citations in every answer', 'Access to core features']
                  : ['Everything in Free', 'Advanced AI models', 'Higher usage limits', 'Priority support']
                ).map((line) => (
                  <li key={line} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <Check size={14} className="mt-0.5 text-brand-500 flex-shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              <button type="button" disabled={busy || isCurrent}
                onClick={() => handleSelect(selectedCategory, tier.tierCode)}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isCurrent
                    ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-default'
                    : highlighted
                      ? 'bg-brand-600 hover:bg-brand-700 text-white'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white'
                } disabled:opacity-60 disabled:cursor-not-allowed`}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                {isCurrent ? 'Current plan' : ctaLabel(tier)}
              </button>
            </div>
          )
        })}
      </div>

      <p className="relative mt-10 text-center text-xs text-gray-400 dark:text-gray-500">
        You can change your plan at any time from account settings.
      </p>
    </div>
  )
}
